import type { DtoV1BoltAuditSectionedEntityAudit } from '~/__generated__/api';
import AccordionContainer from '~/common/components/AccordionContainer';
import Text from '~/common/components/Texts/Text';
import { INCOME_RECONCILIATION } from '~/common/constants';
import theme from '~/theme/';

import AuditTable from './AuditTable';

const accordionProps = {
  h2Style: {
    borderBottomWidth: '1px',
    borderColor: `${theme.colors?.blue['600']}`,
  },
  color: { color: `${theme.colors?.blue['600']}` },
  border: 'none !important',
  mt: 2,
};

type AuditHistoryAccordionProps = {
  data?: DtoV1BoltAuditSectionedEntityAudit[];
};

interface AuditSectionField {
  // Add more specific field types as needed
  [key: string]: any;
}

interface AuditSection {
  type?: string;
  fields?: AuditSectionField[];
  children?: AuditSection[];
}

function renderSection(
  section: AuditSection,
  auditTitle: string,
  isIncomeRecon: boolean = false,
): React.ReactNode {
  if (section?.fields?.length > 0) {
    return (
      <AccordionContainer
        key={`audit-accordion-inner-${section?.type}`}
        header={section?.type}
        {...accordionProps}
      >
        <AuditTable
          key={`audit-table-${section?.type}`}
          data={section.fields}
          section={section}
        />
      </AccordionContainer>
    );
  }

  if (section?.children?.length > 0) {
    return (
      <AccordionContainer
        key={`audit-accordion-inner-${section?.type}`}
        header={section?.type}
        {...accordionProps}
      >
        {section?.children?.map((childSection: AuditSection) =>
          isIncomeRecon ? (
            <AccordionContainer
              key={`audit-child-section-${childSection?.type}`}
              header={childSection?.type}
              {...accordionProps}
            >
              <AuditTable
                key={`audit-table-${section?.type}-${childSection?.type}`}
                data={childSection?.fields}
                isRentalIncomeSection={
                  auditTitle === INCOME_RECONCILIATION.IncomeReconciliation
                }
                section={section}
              />
            </AccordionContainer>
          ) : (
            <div key={`audit-child-section-${childSection?.type}`}>
              <Text
                as="h3"
                sx={{
                  padding: '16px 10px 16px 16px',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  lineHeight: '1.5rem',
                }}
              >
                {childSection?.type}
              </Text>
              <AuditTable
                key={`audit-table-${section?.type}-${childSection?.type}`}
                data={childSection?.fields}
                section={null}
              />
            </div>
          ),
        )}
      </AccordionContainer>
    );
  }
  return null;
}

export default function AuditHistoryAccordion({
  data,
}: Readonly<AuditHistoryAccordionProps>) {
  return (
    <>
      {data?.map((audit) => {
        const isIncomeRecon =
          audit.title === INCOME_RECONCILIATION.IncomeReconciliation;
        if (isIncomeRecon) {
          return audit?.auditSections
            ?.filter(
              (section): section is AuditSection =>
                !!section && typeof section.type === 'string',
            )
            .map((section) => renderSection(section, audit.title, true));
        } else {
          return (
            <AccordionContainer key={audit.title} header={audit.title}>
              <AuditHistorySections
                title={audit.title}
                sections={audit.auditSections}
              />
            </AccordionContainer>
          );
        }
      })}
    </>
  );
}

export function AuditHistorySections({
  title,
  sections,
}: Readonly<{ title?: string; sections: AuditSection[] }>) {
  return (
    <>
      {sections
        ?.filter(
          (section): section is AuditSection =>
            !!section && typeof section.type === 'string',
        )
        .map((section) => renderSection(section, title))}
    </>
  );
}
